import { Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  FORM_CONTROL_BASE_CLASS,
  FORM_CONTROL_FOCUS_CLASS,
} from '@/components/ui/form-control-styles'
import {
  FormAccordion,
  FormAccordionCard,
  FormBadge,
  FormFieldsCountBadge,
  FormInnerBlock,
  FormNeutralBadge,
} from '@/components/contracts/ContractFormAccordion'
import { ServiceCategoryIcon } from '@/components/icons/ServiceCategoryIcons'
import { cn } from '@/lib/utils'
import {
  CategoryChip,
  FieldError,
  FormField,
  RequiredLabel,
} from './FormControls'
import { dashedAddBtnClass, restyleDeleteBtnClass } from './styles'
import type { BringItem } from './types'

const figmaFieldClass = cn(
  'h-10 rounded-xl border px-4 py-3 normal-case leading-[1.4] shadow-none placeholder:font-normal placeholder:text-[#6c7285]',
  FORM_CONTROL_BASE_CLASS,
  FORM_CONTROL_FOCUS_CLASS
)

const figmaCategoryChipClass = cn('text-xs font-normal normal-case leading-4')

const figmaAddButtonClass = cn(dashedAddBtnClass, 'text-xs normal-case')

const figmaDeleteButtonClass = cn(restyleDeleteBtnClass)

interface Props {
  incompleteFields: number
  categoryError?: string
  categoryIds: string[]
  setCategoryIds: (next: string[] | ((prev: string[]) => string[])) => void
  clearCategoryError: () => void
  categoryOptions: { id: string; name: string }[]
  items: BringItem[]
  setItems: (next: BringItem[]) => void
  checklist: string[]
  setChecklist: (next: string[]) => void
}

export function ServiceInformationSection({
  incompleteFields,
  categoryError,
  categoryIds,
  setCategoryIds,
  clearCategoryError,
  categoryOptions,
  items,
  setItems,
  checklist,
  setChecklist,
}: Props) {
  const { t } = useTranslation('contracts')

  return (
    <FormAccordionCard
      value='service-information'
      step={2}
      title={t('form.sections.serviceInformation')}
      complete={incompleteFields === 0}
      stepClassName='font-medium'
      titleClassName='font-semibold normal-case'
      badge={
        incompleteFields === 0 ? (
          <FormBadge className='font-medium'>
            {t('form.categoriesCount', { count: categoryIds.length })}
          </FormBadge>
        ) : (
          <FormFieldsCountBadge
            count={incompleteFields}
            label={t('form.fieldsToFill', {
              count: incompleteFields,
            })}
            completedLabel={t('form.fieldsFilled')}
            className='font-medium'
          />
        )
      }
    >
      <FormAccordion
        defaultValue={['service-categories', 'items-to-bring', 'resume']}
        className='flex flex-col gap-4'
      >
        <FormInnerBlock
          value='service-categories'
          title={t('form.sections.serviceCategories')}
          required
          titleClassName='text-sm'
        >
          {categoryOptions.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              {t('form.serviceCategories.empty')}
            </p>
          ) : (
            <div
              aria-invalid={!!categoryError}
              className={cn(
                'flex flex-wrap gap-2',
                categoryError
              )}
            >
              {categoryOptions.map(cat => {
                const selected = categoryIds.includes(cat.id)
                return (
                  <CategoryChip
                    key={cat.id}
                    selected={selected}
                    onSelect={() => {
                      setCategoryIds(prev =>
                        selected
                          ? prev.filter(id => id !== cat.id)
                          : [...prev, cat.id]
                      )
                      if (!selected) clearCategoryError()
                    }}
                    className={figmaCategoryChipClass}
                  >
                    <span className='flex size-4 shrink-0 items-center justify-center'>
                      <ServiceCategoryIcon name={cat.name} />
                    </span>
                    {cat.name}
                  </CategoryChip>
                )
              })}
            </div>
          )}
          <FieldError message={categoryError} />
        </FormInnerBlock>

        <FormInnerBlock
          value='items-to-bring'
          title={t('form.sections.itemsToBring')}
          titleClassName='text-sm'
          badge={
            <FormNeutralBadge className='font-medium'>
              {t('form.optionalBadge')}
            </FormNeutralBadge>
          }
        >
          {items.map((it, i) => (
            <div key={i} className='flex items-end gap-4'>
              <FormField className='min-w-0 flex-1'>
                <RequiredLabel
                  htmlFor={`item-name-${i}`}
                  compact
                  className='font-medium'
                >
                  {t('form.items.itemLabel')}
                </RequiredLabel>
                <Input
                  id={`item-name-${i}`}
                  value={it.name}
                  placeholder={t('form.items.namePlaceholder')}
                  onChange={e =>
                    setItems(
                      items.map((x, j) =>
                        j === i
                          ? {
                              ...x,
                              service_item_id: null,
                              name: e.target.value,
                            }
                          : x
                      )
                    )
                  }
                  className={figmaFieldClass}
                />
              </FormField>
              <FormField className='w-40 shrink-0'>
                <RequiredLabel
                  htmlFor={`item-qty-${i}`}
                  compact
                  className='font-medium'
                >
                  {t('form.items.quantity')}
                </RequiredLabel>
                <Input
                  id={`item-qty-${i}`}
                  type='number'
                  min={1}
                  value={it.quantity}
                  onChange={e =>
                    setItems(
                      items.map((x, j) =>
                        j === i ? { ...x, quantity: e.target.value } : x
                      )
                    )
                  }
                  className={figmaFieldClass}
                />
              </FormField>
              <Button
                type='button'
                variant='outline'
                size='icon'
                className={figmaDeleteButtonClass}
                onClick={() => setItems(items.filter((_, j) => j !== i))}
              >
                <X className='size-5' />
              </Button>
            </div>
          ))}
          <Button
            type='button'
            variant='outline'
            className={figmaAddButtonClass}
            onClick={() =>
              setItems([
                ...items,
                { service_item_id: null, name: '', quantity: '1' },
              ])
            }
          >
            <Plus className='size-4' />
            {t('form.addNew')}
          </Button>
        </FormInnerBlock>

        <FormInnerBlock
          value='resume'
          title={t('form.sections.interventionResume')}
          titleClassName='text-sm'
          badge={
            <FormNeutralBadge className='font-medium'>
              {t('form.optionalBadge')}
            </FormNeutralBadge>
          }
        >
          {checklist.map((c, i) => (
            <div key={i} className='flex items-end gap-4'>
              <FormField className='min-w-0 flex-1'>
                <RequiredLabel compact className='font-medium'>
                  {t('form.checklist.descriptionPlaceholder')}
                </RequiredLabel>
                <Input
                  placeholder={t('form.checklist.enterPlaceholder')}
                  value={c}
                  onChange={e =>
                    setChecklist(
                      checklist.map((x, j) => (j === i ? e.target.value : x))
                    )
                  }
                  className={figmaFieldClass}
                />
              </FormField>
              <Button
                type='button'
                variant='outline'
                size='icon'
                className={figmaDeleteButtonClass}
                onClick={() =>
                  setChecklist(checklist.filter((_, j) => j !== i))
                }
              >
                <X className='size-5' />
              </Button>
            </div>
          ))}
          <Button
            type='button'
            variant='outline'
            className={figmaAddButtonClass}
            onClick={() => setChecklist([...checklist, ''])}
          >
            <Plus className='size-4' />
            {t('form.addNew')}
          </Button>
        </FormInnerBlock>
      </FormAccordion>
    </FormAccordionCard>
  )
}
